/* =========================================================
   El QR del segundo factor, dibujado aquí.

   El `otpauth://` lleva el secreto TOTP dentro. Mandarlo a una API de
   códigos QR de un tercero —que es lo que hace medio internet— sería
   regalarle a ese tercero el segundo factor de la cuenta. Se genera en
   el navegador y no sale de él.

   `qrcode-generator` calcula la matriz; aquí solo se traduce a un
   trazado SVG. Un solo `<path>` en vez de cientos de `<rect>`: pesa
   menos y el navegador lo dibuja sin costuras entre módulos.
   ========================================================= */
import qrcode from 'qrcode-generator';

export interface Qr {
  /** Módulos por lado, incluido el margen. Va en el viewBox. */
  size: number;
  /** El trazado, listo para el atributo `d` de un `<path>`. */
  path: string;
}

/** Los cuatro módulos de margen que exige la norma. Sin ellos hay
 *  lectores que no encuentran el código sobre un fondo claro. */
const MARGEN = 4;

/** Corrección de errores media: aguanta una pantalla sucia o una foto
 *  torcida sin engordar el código como haría la alta. */
const CORRECCION = 'M' as const;

export function qrMatrix(texto: string): Qr {
  // 0 = que la librería elija la versión más pequeña que quepa. Un
  // otpauth:// con correo largo pasa de la versión 4, así que fijarla
  // a mano fallaría con ciertos usuarios y no con otros.
  const qr = qrcode(0, CORRECCION);
  qr.addData(texto);
  qr.make();

  const n = qr.getModuleCount();
  const partes: string[] = [];
  for (let y = 0; y < n; y++) {
    // Se juntan los módulos negros contiguos de cada fila en un solo
    // rectángulo: menos de la mitad de trazado, y sin líneas de un píxel
    // entre módulos cuando el navegador redondea.
    let inicio = -1;
    for (let x = 0; x <= n; x++) {
      const negro = x < n && qr.isDark(y, x);
      if (negro && inicio < 0) inicio = x;
      if (!negro && inicio >= 0) {
        partes.push(`M${inicio + MARGEN} ${y + MARGEN}h${x - inicio}v1h-${x - inicio}z`);
        inicio = -1;
      }
    }
  }
  return { size: n + MARGEN * 2, path: partes.join('') };
}

/** Atajo cuando solo hace falta el trazado. */
export const qrSvgPath = (texto: string) => qrMatrix(texto).path;
